import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  Users, Search, Download,
  Clock, Building,
  AlertCircle, XCircle,
  Loader2, FileDown,
  ChevronUp,
  ChevronDown,
  BookOpen,
  Award,
  Briefcase
} from 'lucide-react';
import { courseAssignmentAnalyticsService } from '@/services/courseAssignmentAnalytics.service';
import type { UserAnalyticsFilters } from '@/types/courseAssignmentAnalytics.types';
import { userService } from '@/services/user.service';
import { toast } from '@/components/ui/Toast';
import { Select } from '@/components/ui/Select';
import Pagination from '@/components/ui/Pagination';
import { exportService, type ExportUserAnalyticsParams } from '@/services/export.service';
import Modal from '@/components/ui/Modal';
import { useDebounce } from '@/utils/debounce';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { MY_TEAM_PROGRESS_PERMISSION } from '@/constants/constant-permissions';

interface FilterOptions {
  department: string;
  status: 'all' | 'not_started' | 'in_progress' | 'completed' | 'overdue';
  engagement: 'all' | 'high' | 'medium' | 'low';
  sort:'assignedCourses'|'completedPercentage'|'compltedCourseCount'|'certification_count'|'completed_certification_count';
  searchQuery: string;
  page: number;
  limit: number;
  courseId:string;
  certificationId:string;
  teamworkFilter: 'all' | 'bu_head' | 'reporting_manager' | 'hrbp';
  role:string;
  created_from:string;
  created_to:string;
  completed_from:string;
  completed_to:string;
  cert_assigned_from:string;
  cert_assigned_to:string;
  cert_completed_from:string;
  cert_completed_to:string;
  // When true, only show users with at least 1 course or certification assigned
  require_assignments: boolean;
}

interface DepartmentOption {
  group_name: string;
  group_key: string;
  name?: string;
}

interface CourseOptions {
  _id: string;
  title: string;
}

export default function MyTeamProgress() {
  usePermissionRouteAccess([
    MY_TEAM_PROGRESS_PERMISSION.ADMIN_MY_TEAM_PROGRESS_READ,
    MY_TEAM_PROGRESS_PERMISSION.ADMIN_MY_TEAM_PROGRESS_WRITE,
  ]);
  const canWrite = useHasPermission([MY_TEAM_PROGRESS_PERMISSION.ADMIN_MY_TEAM_PROGRESS_WRITE]);
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FilterOptions>({
    department: 'all',
    status: 'all',
    courseId:'',
    certificationId:'',
    teamworkFilter: 'all',
    role: '',
    engagement: 'all',
    searchQuery: '',
    sort:'assignedCourses',
    created_from:'',
    created_to:'',
    completed_from:'',
    completed_to:'',
    cert_assigned_from:'',
    cert_assigned_to:'',
    cert_completed_from:'',
    cert_completed_to:'',
    // When true, only show users with at least 1 course or certification (default: ON)
    require_assignments: true,
    page: 1,
    limit: 10
  });
  const [showFilters, setShowFilters] = useState(false);
  const [exportLoading, setExportLoading] = useState<{
    excel: boolean;
    csv: boolean;
  }>({ excel: false, csv: false });

  // Department & Role modal state
  const [selectedUserDetails, setSelectedUserDetails] = useState<{
    userName: string;
    email: string;
    departments: string[];
    roles: string[];
  } | null>(null);

  // Debounce search query (1 second delay)
  const debouncedSearchQuery = useDebounce(filters.searchQuery, 1000);

  // Set up react-hook-form for select inputs
  const { control, watch, setValue, reset } = useForm<FilterOptions>({
    defaultValues: filters
  });

  // Watch form changes and update filters state
  useEffect(() => {
    const subscription = watch((formData, { name }) => {
      setFilters(prev => {
        const newFilters: FilterOptions = {
          ...prev,
          ...(formData as FilterOptions)
        };

        // Reset to page 1 when filters change (except page and searchQuery)
        if (name && name !== 'page' && name !== 'searchQuery') {
          newFilters.page = 1;
        }

        return newFilters;
      });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Auto-reset role to "All Roles" when department is changed to "All Departments"
  useEffect(() => {
    if (filters.department === 'all' && filters.role !== '') {
      setValue('role', '');
      setFilters(prev => ({ ...prev, role: '', page: 1 }));
    }
  }, [filters.department, filters.role, setValue]);


  // Convert date string to UTC start of day (00:00:00.000Z)
  const toISOStringIfValid = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    // Parse date as YYYY-MM-DD and create UTC date at start of day
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  };

  // Convert "to" date to UTC end of day (23:59:59.999Z)
  const toEndOfDayISO = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    // Parse date as YYYY-MM-DD and create UTC date at end of day
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  };

  // Build API filters from UI filters
  const buildApiFilters = (): UserAnalyticsFilters => {
    const apiFilters: UserAnalyticsFilters = {
      page: filters.page,
      limit: filters.limit,
    };

    // Use debounced search query for API call
    if (debouncedSearchQuery) {
      apiFilters.search = debouncedSearchQuery;
    }

    if (filters.status !== 'all') {
      apiFilters.status = filters.status;
    }

    if (filters.engagement !== 'all') {
      apiFilters.engagement = filters.engagement;
    }
    if (filters.sort) apiFilters.sort = filters.sort as any;

    // Course date filters - only include if BOTH from and to are selected
    if (filters.created_from && filters.created_to) {
      apiFilters.created_from = toISOStringIfValid(filters.created_from);
      apiFilters.created_to = toEndOfDayISO(filters.created_to);
    }

    if (filters.completed_from && filters.completed_to) {
      apiFilters.completed_from = toISOStringIfValid(filters.completed_from);
      apiFilters.completed_to = toEndOfDayISO(filters.completed_to);
    }

    if (filters.department !== 'all') {
      // Find the selected department's groupKey
      const selectedDept = departmentsData?.data?.find((dept: DepartmentOption) =>
        (dept.group_name || dept.name) === filters.department
      );
      if (selectedDept?.group_key) {
        apiFilters.groupKey = selectedDept.group_key;
      }
       if (filters.role) {
        apiFilters.roleKey = filters.role;
      }
    }
    if (filters.courseId && filters.courseId !== 'all') {
      apiFilters.courseId = filters.courseId;
    }

    // Certification filters
    if (filters.certificationId && filters.certificationId !== 'all') {
      apiFilters.assigned_certifications = filters.certificationId;
    }

    // Teamwork filter - always send to backend (required field)
    apiFilters.teamwork_filter = filters.teamworkFilter;

    // Certification date filters - only include if BOTH from and to are selected
    if (filters.cert_assigned_from && filters.cert_assigned_to) {
      apiFilters.cert_assigned_from = toISOStringIfValid(filters.cert_assigned_from);
      apiFilters.cert_assigned_to = toEndOfDayISO(filters.cert_assigned_to);
    }

    if (filters.cert_completed_from && filters.cert_completed_to) {
      apiFilters.cert_completed_from = toISOStringIfValid(filters.cert_completed_from);
      apiFilters.cert_completed_to = toEndOfDayISO(filters.cert_completed_to);
    }

    // Require assignments filter
    apiFilters.require_assignments = filters.require_assignments;

    return apiFilters;
  };

  // Fetch user analytics data
  const { data: analyticsData, isLoading, error, refetch } = useQuery({
    queryKey: ['my-team-progress', {
      ...filters,
      searchQuery: debouncedSearchQuery // Use debounced search query in cache key
    }],
    queryFn: () => courseAssignmentAnalyticsService.getUserAnalytics(buildApiFilters()),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });


  // Handle export functionality - uses new export service
  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(prev => ({ ...prev, [format]: true }));

      // Build export filters from current UI filters (same as API call but without pagination)
      const exportFilters: ExportUserAnalyticsParams = {
        reportName: 'team-progress' // Custom filename for My Team Progress exports
      };

      // Search filter - use debounced search query
      if (debouncedSearchQuery) {
        exportFilters.search = debouncedSearchQuery;
      }

      // Status filter
      if (filters.status !== 'all') {
        exportFilters.status = filters.status;
      }

      // Engagement filter
      if (filters.engagement !== 'all') {
        exportFilters.engagement = filters.engagement;
      }

      // Sort
      if (filters.sort) {
        exportFilters.sort = filters.sort as any;
      }

      // Department and Role filters
      if (filters.department !== 'all') {
        const selectedDept = departmentsData?.data?.find((dept: DepartmentOption) =>
          (dept.group_name || dept.name) === filters.department
        );
        if (selectedDept?.group_key) {
          exportFilters.groupKey = selectedDept.group_key;
        }
        if (filters.role) {
          exportFilters.roleKey = filters.role;
        }
      }

      // Course filter
      if (filters.courseId && filters.courseId !== 'all') {
        exportFilters.courseId = filters.courseId;
      }

      // Certification filter
      if (filters.certificationId && filters.certificationId !== 'all') {
        exportFilters.assigned_certifications = filters.certificationId;
      }

      // Teamwork filter - always send to backend (required field)
      exportFilters.teamwork_filter = filters.teamworkFilter;

      // Course date filters
      if (filters.created_from && filters.created_to) {
        exportFilters.created_from = toISOStringIfValid(filters.created_from);
        exportFilters.created_to = toEndOfDayISO(filters.created_to);
      }

      if (filters.completed_from && filters.completed_to) {
        exportFilters.completed_from = toISOStringIfValid(filters.completed_from);
        exportFilters.completed_to = toEndOfDayISO(filters.completed_to);
      }

      // Certification date filters
      if (filters.cert_assigned_from && filters.cert_assigned_to) {
        exportFilters.cert_assigned_from = toISOStringIfValid(filters.cert_assigned_from);
        exportFilters.cert_assigned_to = toEndOfDayISO(filters.cert_assigned_to);
      }

      if (filters.cert_completed_from && filters.cert_completed_to) {
        exportFilters.cert_completed_from = toISOStringIfValid(filters.cert_completed_from);
        exportFilters.cert_completed_to = toEndOfDayISO(filters.cert_completed_to);
      }

      // Require assignments filter
      exportFilters.require_assignments = filters.require_assignments;

      // Call export service
      const result = await exportService.exportUserAnalytics(format, exportFilters);

      if (!result.success) {
        toast.error(result.message || 'No data available to export');
        return;
      }

      toast.success(`${result.count} team member records exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error(`Failed to export data as ${format.toUpperCase()}`);
      console.error('Export error:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Transform API data for display
  const employees = analyticsData?.users || [];

  // Fetch departments/groups dynamically
  const { data: departmentsData } = useQuery({
    queryKey: ['departments-dropdown'],
    queryFn: () => userService.getGroupsDropdown(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

    const { data: CourseData } = useQuery({
    queryKey: ['course-dropdown'],
    queryFn: () => userService.getCourseDropdown(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch certification dropdown data
  const { data: certificationDropdownData } = useQuery({
    queryKey: ['certification-dropdown'],
    queryFn: () => userService.getCertificationDropdown(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Build departments list from API or use fallback
  const departments = departmentsData?.data
    ? ['All', ...departmentsData.data.map((dept: DepartmentOption) => dept.group_name || dept.name || 'Unknown')]
    : ['All'];

  const courses = CourseData?.data || [];
  const certifications = certificationDropdownData?.data || [];

  // Options for Select components
  const courseOptions = [
    { value: 'all', label: 'All Courses' },
    ...courses.map((course: CourseOptions) => ({
      value: course._id,
      label: course.title
    }))
  ];

  const certificationOptions = [
    { value: 'all', label: 'All Certifications' },
    ...certifications.map((certification: any) => ({
      value: certification.certification_id || certification._id || certification.id,
      label: certification.title || certification.name || 'Unknown'
    }))
  ];

  // Team View filter options - filters data based on logged-in user's role
  const teamworkFilterOptions = [
    { value: 'all', label: 'All Team Members' },
    { value: 'bu_head', label: 'Business Unit' },
    { value: 'reporting_manager', label: 'Direct Reports' },
    { value: 'hrbp', label: 'HR Business Partner' }
  ];

  const departmentOptions = departments.map((dept, index) => ({
    value: index === 0 ? 'all' : dept,
    label: dept === 'All' ? 'All Departments' : dept
  }));

  const engagementOptions = [
    { value: 'all', label: 'All Engagement' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const sortOptions = [
    { value: 'assignedCourses', label: 'Assigned Courses' },
    { value: 'completedPercentage', label: 'Completed Percentage' },
    { value: 'completedCourseCount', label: 'Completed Course Count' },
    { value: 'certification_count', label: 'Certification Count' },
    { value: 'completed_certification_count', label: 'Completed Certification Count' },
  ];



  // Get role options based on selected department
  const roleOptions = filters.department && filters.department !== 'all'
    ? [
        { value: '', label: 'All Roles' },
        ...(departmentsData?.data
          ?.find((dept: DepartmentOption) =>
            (dept.group_name || dept.name) === filters.department
          )
          ?.roles?.map((role: { role_key: string; role_name: string }) => ({
            value: role.role_key,
            label: role.role_name || role.role_key
          })) || [])
      ]
    : [{ value: '', label: 'All Roles' }];

  const engagementLevels = [
    { value: 'all', label: 'All Engagement' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const sortingLevels = [
    { value: 'assignedCourses', label: 'Assigned Courses' },
    { value: 'completedPercentage', label: 'Completed Percentage' },
    { value: 'completedCourseCount', label: 'Completed Course Count' },
    { value: 'certification_count', label: 'Certification Count' },
    { value: 'completed_certification_count', label: 'Completed Certification Count' },
  ]
  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'bg-green-100 text-green-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTimeSpent = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);              // Get total hours
    const mins = Math.floor((seconds % 3600) / 60);        // Get remaining minutes
    const secs = Math.floor(seconds % 60);                 // Get remaining seconds

    return `${hours.toString().padStart(2, '0')}:` +
          `${mins.toString().padStart(2, '0')}:` +
          `${secs.toString().padStart(2, '0')}`;
  };

  const formatProgress = (progress: number): string => {
    if (progress === 0) return '0%';
    if (progress % 1 === 0) return `${progress}%`; // Whole number
    return `${progress.toFixed(2).replace(/\.?0+$/, '')}%`; // Remove trailing zeros
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header - Teal/Emerald Theme */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">My Team Progress</h1>
            <p className="text-teal-100">
              Monitor your team's learning journey, track achievements, and support their growth
            </p>
          </div>
          <div className="flex gap-3">
            <div className="flex gap-2">
              <button
                onClick={() => canWrite && handleExport('excel')}
                disabled={!canWrite || exportLoading.excel}
                className="flex items-center gap-2 px-4 py-2 bg-white text-teal-600 rounded-lg hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : 'Export as Excel'}
              >
                {exportLoading.excel ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FileDown className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">
                  {exportLoading.excel ? 'Exporting...' : 'Excel'}
                </span>
              </button>
              <button
                onClick={() => canWrite && handleExport('csv')}
                disabled={!canWrite || exportLoading.csv}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canWrite ? 'You do not have permission' : 'Export as CSV'}
              >
                {exportLoading.csv ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">
                  {exportLoading.csv ? 'Exporting...' : 'CSV'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Left: Title */}
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter & Search</h2>
          </div>

          {/* Right: Toggle + Clear All + Show/Hide Filters */}
          <div className="flex items-center gap-3">
            {/* Toggle: Show only assigned users */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Assigned Only</span>
              <button
                onClick={() => setFilters(prev => ({ ...prev, require_assignments: !prev.require_assignments, page: 1 }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  filters.require_assignments ? 'bg-teal-600' : 'bg-gray-300'
                }`}
                title={filters.require_assignments ? 'Showing users with assignments only' : 'Showing all users'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    filters.require_assignments ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="h-6 w-px bg-gray-200" />

            <button
              onClick={() => {
                const defaultFilters: FilterOptions = {
                  department: 'all',
                  status: 'all',
                  engagement: 'all',
                  searchQuery: '',
                  sort: 'assignedCourses',
                  courseId: '',
                  certificationId: '',
                  teamworkFilter: 'all',
                  page: 1,
                  limit: 10,
                  role: '',
                  created_from: '',
                  created_to: '',
                  completed_from: '',
                  completed_to: '',
                  cert_assigned_from: '',
                  cert_assigned_to: '',
                  cert_completed_from: '',
                  cert_completed_to: '',
                  require_assignments: true,
                };
                setFilters(defaultFilters);
                reset(defaultFilters);
              }}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700 hover:bg-gray-100 transition"
            >
              Clear All
            </button>

            <button
              onClick={() => setShowFilters(prev => !prev)}
              className="flex items-center gap-1 px-3 py-1 text-sm text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Search Input */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Team Members
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email or employee id"
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value, page: 1 })}
                className="w-full pl-10 pr-10 h-[42px] text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                style={{ minHeight: '42px' }}
              />
              {filters.searchQuery && (
                <button
                  onClick={() => setFilters({ ...filters, searchQuery: '', page: 1 })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Course Filter */}
          <div>
            <Select
              name="courseId"
              control={control}
              label="Select Course"
              options={courseOptions}
              isSearchable={true}
              placeholder="Select a course"
            />
            {filters.courseId && filters.courseId !== 'all' && (
              <div className="mt-2 text-xs text-teal-600">
                Filtered: {courses.find((c: CourseOptions) => c._id === filters.courseId)?.title || 'Unknown Course'}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Certification Filter */}
          <div>
            <Select
              name="certificationId"
              control={control}
              label="Select Certification"
              options={certificationOptions}
              isSearchable={true}
              placeholder="Select a certification"
            />
            {filters.certificationId && filters.certificationId !== 'all' && (
              <div className="mt-2 text-xs text-teal-600">
                Filtered: {certifications.find((c: any) => (c.certification_id || c._id || c.id) === filters.certificationId)?.title || certifications.find((c: any) => (c.certification_id || c._id || c.id) === filters.certificationId)?.name || 'Unknown Certification'}
              </div>
            )}
          </div>

          {/* Team View Filter */}
          <div>
            <Select
              name="teamworkFilter"
              control={control}
              label="Team View"
              options={teamworkFilterOptions}
              isSearchable={false}
              placeholder="Select team view"
            />
            {filters.teamworkFilter && filters.teamworkFilter !== 'all' && (
              <div className="mt-2 text-xs text-teal-600">
                Viewing: {teamworkFilterOptions.find(t => t.value === filters.teamworkFilter)?.label}
              </div>
            )}
          </div>
        </div>

          {/* Filter Dropdowns */}
          {showFilters && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Department Filter */}
            <div>
              <Select
                name="department"
                control={control}
                label="Department"
                options={departmentOptions}
                isSearchable={true}
                placeholder="Select department"
              />
              {filters.department !== 'all' && (
                <div className="mt-2 text-xs text-teal-600">
                  Filtered: {filters.department === 'all' ? 'All' : filters.department}
                </div>
              )}
            </div>

            {/* Role Filter */}
            <div>
              <Select
                name="role"
                control={control}
                label="Role"
                options={roleOptions}
                isSearchable={true}
                placeholder="Select role"
                isDisabled={filters.department === 'all' || !filters.department}
              />
              {/* Selected Role Summary */}
              {filters.role && (
                <div className="mt-2 text-xs text-teal-600">
                  Selected Role:{' '}
                  {
                    departmentsData?.data
                      ?.find(
                        (dept: DepartmentOption) =>
                          (dept.group_name || dept.name) === filters.department
                      )
                      ?.roles?.find(
                        (role: { role_key: string }) => role.role_key === filters.role
                      )?.role_name
                  }
                </div>
              )}
            </div>
          </div>

          {/* Sort By & Engagement Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sort By Filter */}
            <div>
              <Select
                name="sort"
                control={control}
                label="Sort By"
                options={sortOptions}
                isSearchable={false}
                placeholder="Select sort option"
              />
              {/* Sorted By Pill */}
              {filters.sort && (
                <div className="mt-2 text-xs text-teal-600">
                  Sorted by: {sortingLevels.find(s => s.value === filters.sort)?.label}
                </div>
              )}
            </div>

            {/* Engagement Filter */}
            <div>
              <Select
                name="engagement"
                control={control}
                label="Engagement Level"
                options={engagementOptions}
                isSearchable={false}
                placeholder="Select engagement level"
              />
              {filters.engagement !== 'all' && (
                <div className="mt-2 text-xs text-teal-600">
                  Filtered: {engagementLevels.find(e => e.value === filters.engagement)?.label}
                </div>
              )}
            </div>
          </div>
        <div className="grid grid-cols-2 gap-6">
            {/* Created Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Course Assigned Date Range
              </label>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <span className="block text-xs text-gray-500 mb-1">From</span>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={filters.created_from || ''}
                    onChange={(e) =>
                      setFilters({ ...filters, created_from: e.target.value, page: 1 })
                    }
                    max={filters.created_to || undefined}
                  />
                </div>
                <div className="flex-1">
                  <span className="block text-xs text-gray-500 mb-1">To</span>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none ${
                      filters.created_from
                        ? 'focus:ring-2 focus:ring-teal-500'
                        : 'bg-gray-100 cursor-not-allowed opacity-60'
                    }`}
                    value={filters.created_to || ''}
                    onChange={(e) =>
                      setFilters({ ...filters, created_to: e.target.value, page: 1 })
                    }
                    min={filters.created_from || undefined}
                    disabled={!filters.created_from}
                  />
                </div>
              </div>
              {(filters.created_from || filters.created_to) && (
                <div className="mt-2 text-xs text-teal-600">
                  Filtered: {filters.created_from || 'Any'} → {filters.created_to || 'Any'}
                </div>
              )}
            </div>

            {/* Completed Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Course Completed Date Range
              </label>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <span className="block text-xs text-gray-500 mb-1">From</span>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={filters.completed_from || ''}
                    onChange={(e) => {
                      setFilters({ ...filters, completed_from: e.target.value, page: 1 });
                      document.getElementById('completed_to_input')?.focus();
                    }}
                    max={filters.completed_to || undefined}
                  />
                </div>
                <div className="flex-1">
                  <span className="block text-xs text-gray-500 mb-1">To</span>
                  <input
                    id="completed_to_input"
                    type="date"
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none ${
                      filters.completed_from
                        ? 'focus:ring-2 focus:ring-teal-500'
                        : 'bg-gray-100 cursor-not-allowed opacity-60'
                    }`}
                    value={filters.completed_to || ''}
                    onChange={(e) =>
                      setFilters({ ...filters, completed_to: e.target.value, page: 1 })
                    }
                    min={filters.completed_from || undefined}
                    disabled={!filters.completed_from}
                  />
                </div>
              </div>
              {(filters.completed_from || filters.completed_to) && (
                <div className="mt-2 text-xs text-teal-600">
                  Filtered: {filters.completed_from || 'Any'} →{' '}
                  {filters.completed_to || 'Any'}
                </div>
              )}
            </div>
          </div>

          {/* Certification Date Ranges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Certification Assignment Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Certification Assigned Date Range
                </label>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">From</span>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={filters.cert_assigned_from || ''}
                      onChange={(e) => setFilters({ ...filters, cert_assigned_from: e.target.value, page: 1 })}
                      max={filters.cert_assigned_to || undefined}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">To</span>
                    <input
                      type="date"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none ${
                        filters.cert_assigned_from
                          ? 'focus:ring-2 focus:ring-teal-500'
                          : 'bg-gray-100 cursor-not-allowed opacity-60'
                      }`}
                      value={filters.cert_assigned_to || ''}
                      onChange={(e) => setFilters({ ...filters, cert_assigned_to: e.target.value, page: 1 })}
                      min={filters.cert_assigned_from || undefined}
                      disabled={!filters.cert_assigned_from}
                    />
                  </div>
                </div>
                {(filters.cert_assigned_from || filters.cert_assigned_to) && (
                  <div className="mt-2 text-xs text-teal-600">
                    Filtered: {filters.cert_assigned_from || 'Any'} → {filters.cert_assigned_to || 'Any'}
                  </div>
                )}
              </div>

              {/* Certification Completion Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Certification Completed Date Range
                </label>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">From</span>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={filters.cert_completed_from || ''}
                      onChange={(e) => setFilters({ ...filters, cert_completed_from: e.target.value, page: 1 })}
                      max={filters.cert_completed_to || undefined}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">To</span>
                    <input
                      type="date"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none ${
                        filters.cert_completed_from
                          ? 'focus:ring-2 focus:ring-teal-500'
                          : 'bg-gray-100 cursor-not-allowed opacity-60'
                      }`}
                      value={filters.cert_completed_to || ''}
                      onChange={(e) => setFilters({ ...filters, cert_completed_to: e.target.value, page: 1 })}
                      min={filters.cert_completed_from || undefined}
                      disabled={!filters.cert_completed_from}
                    />
                  </div>
                </div>
                {(filters.cert_completed_from || filters.cert_completed_to) && (
                  <div className="mt-2 text-xs text-teal-600">
                    Filtered: {filters.cert_completed_from || 'Any'} → {filters.cert_completed_to || 'Any'}
                  </div>
                )}
              </div>
            </div>
          </>
          )}
          {/* Active Filters Summary */}
            {(filters.searchQuery ||
              filters.department !== 'all' ||
              filters.role !== '' ||
              filters.engagement !== 'all' ||
              filters.courseId !== 'all' ||
              filters.certificationId !== 'all' ||
              filters.teamworkFilter !== 'all' ||
              filters.sort !== 'assignedCourses' ||
              filters.created_from ||
              filters.created_to ||
              filters.completed_from ||
              filters.completed_to ||
              filters.cert_assigned_from ||
              filters.cert_assigned_to ||
              filters.cert_completed_from ||
              filters.cert_completed_to ||
              true  // Always show to display sort by default
            ) && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-700">Active filters:</span>
                {filters.searchQuery && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                    Search: "{filters.searchQuery}"
                    <button
                      onClick={() => setFilters({ ...filters, searchQuery: '', page: 1 })}
                      className="hover:text-teal-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.department !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    Dept: {filters.department}
                    <button
                      onClick={() => {
                        setValue('department', 'all');
                        setValue('role', '');
                        setFilters({ ...filters, department: 'all', role: '', page: 1 });
                      }}
                      className="hover:text-emerald-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.courseId && filters.courseId !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                    Course: {courses.find((c: CourseOptions) => c._id === filters.courseId)?.title || filters.courseId}
                    <button
                      onClick={() => setFilters({ ...filters, courseId: '', page: 1 })}
                      className="hover:text-cyan-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.certificationId && filters.certificationId !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                    Certification: {certifications.find((c: any) => (c.certification_id || c._id || c.id) === filters.certificationId)?.title || certifications.find((c: any) => (c.certification_id || c._id || c.id) === filters.certificationId)?.name || filters.certificationId}
                    <button
                      onClick={() => setFilters({ ...filters, certificationId: '', page: 1 })}
                      className="hover:text-amber-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.teamworkFilter && filters.teamworkFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                    Team View: {teamworkFilterOptions.find(t => t.value === filters.teamworkFilter)?.label}
                    <button
                      onClick={() => {
                        setValue('teamworkFilter', 'all');
                        setFilters({ ...filters, teamworkFilter: 'all', page: 1 });
                      }}
                      className="hover:text-indigo-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.role !== '' && (
                   <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    Role: {
                      departmentsData?.data
                        ?.find((dept: DepartmentOption) =>
                          (dept.group_name || dept.name) === filters.department
                        )
                        ?.roles?.find((role: { role_key: string; role_name: string }) =>
                          role.role_key === filters.role
                        )?.role_name || filters.role
                    }
                    <button
                      onClick={() => {
                        setValue('role', '');
                        setFilters({ ...filters, role: '', page: 1 });
                      }}
                      className="hover:text-emerald-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.engagement !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Engagement: {engagementLevels.find(e => e.value === filters.engagement)?.label}
                    <button
                      onClick={() => {
                        setValue('engagement', 'all');
                        setFilters({ ...filters, engagement: 'all', page: 1 });
                      }}
                      className="hover:text-purple-900"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Sort: {sortingLevels.find(e => e.value === filters.sort)?.label || 'Assigned Courses'}
                    {filters.sort !== 'assignedCourses' && (
                      <button
                        onClick={() => setFilters({ ...filters, sort: 'assignedCourses', page: 1 })}
                        className="hover:text-purple-900"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                {/* Created Date Range */}
                  {(filters.created_from || filters.created_to) && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                      Course Assigned: {filters.created_from || 'Any'} - {filters.created_to || 'Any'}
                      <button
                        onClick={() => setFilters({ ...filters, created_from: '', created_to: '', page: 1 })}
                        className="hover:text-yellow-900"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </span>
                  )}

                  {/* Completed Date Range */}
                  {(filters.completed_from || filters.completed_to) && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-700 text-xs rounded-full">
                      Course Completed: {filters.completed_from || 'Any'} - {filters.completed_to || 'Any'}
                      <button
                        onClick={() => setFilters({ ...filters, completed_from: '', completed_to: '', page: 1 })}
                        className="hover:text-pink-900"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </span>
                  )}

                  {/* Certification Assigned Date Range */}
                  {(filters.cert_assigned_from || filters.cert_assigned_to) && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      Cert Assigned: {filters.cert_assigned_from || 'Any'} - {filters.cert_assigned_to || 'Any'}
                      <button
                        onClick={() => setFilters({ ...filters, cert_assigned_from: '', cert_assigned_to: '', page: 1 })}
                        className="hover:text-orange-900"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </span>
                  )}

                  {/* Certification Completed Date Range */}
                  {(filters.cert_completed_from || filters.cert_completed_to) && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                      Cert Completed: {filters.cert_completed_from || 'Any'} - {filters.cert_completed_to || 'Any'}
                      <button
                        onClick={() => setFilters({ ...filters, cert_completed_from: '', cert_completed_to: '', page: 1 })}
                        className="hover:text-teal-900"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </span>
                  )}

              </div>
            </div>
          )}
        </div>
      </div>
      {/* Team Member List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : error ? (
          <div className="text-center p-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">Failed to load team data</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Retry
            </button>
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center p-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No team members found matching the filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Member</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee Id</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department & Role</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Courses Assigned</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completed Courses</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completion percentage</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Time Spent for Course (HH:MM:SS)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Certifications Assigned</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Certifications Completed</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Time Spent for Certification (HH:MM:SS)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Streak</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Engagement</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Last Active</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employees.map((employee) => {
                    const departments = employee.department
                      ? employee.department.split(',').map(d => d.trim()).filter(d => d.length > 0)
                      : [];
                    const roles = (employee as any).role
                      ? (employee as any).role.split(',').map((r: string) => r.trim()).filter((r: string) => r.length > 0)
                      : [];
                    const extraCount = (departments.length > 1 ? departments.length - 1 : 0) + (roles.length > 1 ? roles.length - 1 : 0);

                    return (
                    <tr key={employee.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                            {employee.profile_image ? (
                              <img
                                src={employee.profile_image}
                                alt={employee.userName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              employee.userName.split(' ').map(n => n[0]).join('').toUpperCase()
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{employee.userName}</p>
                            <p className="text-xs text-gray-500">{employee.email}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{employee.employeeID || 'N/A'}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Primary Department */}
                          {departments.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                {departments[0]}
                              </span>
                            </span>
                          )}

                          {/* Primary Role */}
                          {roles.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                                {roles[0]}
                              </span>
                            </span>
                          )}

                          {/* Show more button */}
                          {extraCount > 0 && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedUserDetails({
                                  userName: employee.userName,
                                  email: employee.email,
                                  departments,
                                  roles
                                });
                              }}
                              className="text-xs text-teal-600 font-medium hover:text-teal-800 hover:underline"
                            >
                              +{extraCount} more
                            </button>
                          )}

                          {departments.length === 0 && roles.length === 0 && (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{employee.assignedCourses || 0}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">{employee.completedCourseCount || 0 }</span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-medium text-gray-900">{formatProgress(employee.progress)}</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${employee.progress}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-medium text-gray-900">{formatProgress(employee.completedPercentage)}</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-emerald-500 h-2 rounded-full"
                              style={{ width: `${employee.completedPercentage}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{formatTimeSpent(employee.timeSpent)}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span className="text-sm text-gray-600">{employee.certification_count || 0}</span>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span className="text-sm font-medium text-emerald-600">{employee.completed_certification_count || 0}</span>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{formatTimeSpent(employee.certification_time_spent || 0)}</span>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex gap-2">
                            <span className="text-sm font-semibold text-teal-600">🔥 Current:{employee.streak.current}</span>
                            <span className="text-sm text-gray-900"></span>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEngagementColor(employee.engagement)}`}>
                          {employee.engagement.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm text-gray-600">{formatDate(employee.lastActive)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              navigate(`/admin/users/${employee.userId}/courses`,
                              { state: { from: '/admin/analytics/my-team-progress'  } })
                            }}
                            className="p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors"
                            title="View Courses"
                          >
                            <BookOpen className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/admin/users/${employee.userId}/certifications`,
                              { state: { from: '/admin/analytics/my-team-progress'  } })
                            }}
                            className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Certifications"
                          >
                            <Award className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {analyticsData?.pagination && (
              <Pagination
                currentPage={filters.page}
                totalPages={Math.ceil(analyticsData.pagination.total / filters.limit)}
                totalItems={analyticsData.pagination.total}
                itemsPerPage={filters.limit}
                onPageChange={(page) => setFilters({ ...filters, page })}
                onItemsPerPageChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </div>

      {/* Department & Role Details Modal */}
      <Modal
        isOpen={!!selectedUserDetails}
        onClose={() => setSelectedUserDetails(null)}
        title="Department Details"
        subtitle={selectedUserDetails ? `${selectedUserDetails.userName} (${selectedUserDetails.email})` : ''}
        size="md"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedUserDetails(null)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedUserDetails && (
          <div className="space-y-6">
            {/* Departments Section */}
            {selectedUserDetails.departments.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building className="w-5 h-5 text-teal-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Departments</h4>
                </div>
                <div className="space-y-2">
                  {selectedUserDetails.departments.map((dept, idx) => (
                    <div
                      key={`dept-${idx}`}
                      className="border border-gray-200 bg-gray-50 rounded-lg overflow-hidden"
                    >
                      <div className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">
                            {dept}
                          </h3>
                        </div>
                        <Building className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Roles Section */}
            {selectedUserDetails.roles.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Roles</h4>
                </div>
                <div className="space-y-2">
                  {selectedUserDetails.roles.map((role, idx) => (
                    <div
                      key={`role-${idx}`}
                      className="border border-gray-200 bg-gray-50 rounded-lg overflow-hidden"
                    >
                      <div className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">
                            {role}
                          </h3>
                        </div>
                        <Briefcase className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
